/**
 * Small line pictograms, drawn inline rather than pulled from an icon package.
 *
 * Every one of them inherits `currentColor` and carries `aria-hidden`, because
 * an icon here always sits beside its own text label -- it is reinforcement
 * for someone scanning the row, never the only thing saying what a control
 * does (section 18). The one exception is the calendar link in the schedule
 * toolbar, which is icon-only and therefore labels itself.
 *
 * They share the geometry of the header's icons in App.tsx: a 24-unit box,
 * 1.7 stroke, round caps -- so a toolbar icon and a header icon look like they
 * came from the same hand.
 */

const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: 'false',
} as const;

/** Rows with a leading marker: the list view. */
export function ListIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <circle cx="4.5" cy="6" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="18" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Bars staggered along a timeline: the Gantt. The offset is the whole point --
    three equal bars would read as the list icon again. */
export function GanttIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <rect x="3" y="4.5" width="9" height="4" rx="1.4" />
      <rect x="8" y="10" width="11" height="4" rx="1.4" />
      <rect x="5" y="15.5" width="8" height="4" rx="1.4" />
    </svg>
  );
}

/** Sheets stacked in plan: the map's layers. */
export function LayersIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M12 3.5 3.5 8l8.5 4.5L20.5 8z" />
      <path d="M3.5 12.4 12 16.9l8.5-4.5" />
      <path d="M3.5 16.6 12 21.1l8.5-4.5" />
    </svg>
  );
}

/**
 * A reticle: put the camera back on what is being shown.
 *
 * Deliberately not a magnifier or a "home". A magnifier means search, which is
 * the control immediately to its left; and "home" would promise a fixed place,
 * where this frames whatever the current view holds.
 */
export function FitIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 3v3.2M12 17.8V21M3 12h3.2M17.8 12H21" />
    </svg>
  );
}

/** A funnel: narrowing a list down to part of itself. */
export function FilterIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M4 5h16l-6.2 7.4v5.4l-3.6 2v-7.4z" />
    </svg>
  );
}

/** Columns of cards: the board. */
export function KanbanIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <rect x="3" y="4" width="5" height="16" rx="1.4" />
      <rect x="9.5" y="4" width="5" height="10" rx="1.4" />
      <rect x="16" y="4" width="5" height="13" rx="1.4" />
    </svg>
  );
}

/** Edit mode. */
export function PencilIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M4 20h4l10.5-10.5a2.6 2.6 0 0 0-3.7-3.7L4.3 16.3z" />
      <path d="M14.2 7.3l3.2 3.2" />
    </svg>
  );
}

/** Adding something. */
export function PlusIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/* --- the three things the map can be drawn with --- */

/** A single place: the pin, drawn as the map draws it. */
export function PointIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M12 21c4.2-4.6 6.3-8 6.3-10.4A6.3 6.3 0 0 0 5.7 10.6C5.7 13 7.8 16.4 12 21z" />
      <circle cx="12" cy="10.4" r="2.2" />
    </svg>
  );
}

/** A route: segments between corners, with the corners shown. */
export function LineIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M5 18l5-8 4 4 5-9" />
      <circle cx="5" cy="18" r="1.8" />
      <circle cx="19" cy="5" r="1.8" />
    </svg>
  );
}

/** An enclosed area: the ring closes, which is the whole difference. */
export function AreaIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M6 5l12 3-2.5 11L4 15.5z" />
    </svg>
  );
}

/** The working calendar behind every calculated date. */
export function CalendarIcon({ size = 17 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <rect x="3.2" y="5" width="17.6" height="16" rx="2.4" />
      <path d="M3.2 9.6h17.6M8 3v4M16 3v4" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Header icons                                                               */
/*                                                                            */
/* These lived inline in App.tsx, which is where the comment at the top of    */
/* this file got its note that the toolbar icons "share the geometry of the   */
/* header's icons in App.tsx" -- an acknowledged duplication of the same       */
/* twenty-four-unit box and 1.7 stroke in two places. They are here now, so    */
/* there is one set of pictograms rather than two that merely agree today.     */
/*                                                                            */
/* They default to 20 rather than 15: the header sits at a larger scale than  */
/* a toolbar control, and that difference was the only real reason the two     */
/* sets were ever apart.                                                      */
/* -------------------------------------------------------------------------- */

/** Inherits `currentColor`, so it follows the header's own text colour. */
export function MailIcon({ size = 20 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
      <path d="M3 7l8.15 5.6a1.5 1.5 0 0 0 1.7 0L21 7" />
    </svg>
  );
}

export function PersonIcon({ size = 20 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.5 20c1.1-3.8 4.2-6 7.5-6s6.4 2.2 7.5 6" />
    </svg>
  );
}

/** Sliders: configuration of the installation, not of a page within it. */
export function SlidersIcon({ size = 20 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M4 6h9M17.5 6H20M4 12h3M11.5 12H20M4 18h9M17.5 18H20" />
      <circle cx="15" cy="6" r="2.1" />
      <circle cx="9" cy="12" r="2.1" />
      <circle cx="15" cy="18" r="2.1" />
    </svg>
  );
}

export function GearIcon({ size = 20 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <circle cx="12" cy="12" r="3.1" />
      <path d="M19.4 14.6a1.6 1.6 0 0 0 .32 1.77l.06.06a1.9 1.9 0 1 1-2.69 2.69l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-.97 1.47v.17a1.9 1.9 0 0 1-3.8 0v-.09a1.6 1.6 0 0 0-1.05-1.47 1.6 1.6 0 0 0-1.77.32l-.06.06a1.9 1.9 0 1 1-2.69-2.69l.06-.06a1.6 1.6 0 0 0 .32-1.77 1.6 1.6 0 0 0-1.47-.97h-.17a1.9 1.9 0 0 1 0-3.8h.09a1.6 1.6 0 0 0 1.47-1.05 1.6 1.6 0 0 0-.32-1.77l-.06-.06a1.9 1.9 0 1 1 2.69-2.69l.06.06a1.6 1.6 0 0 0 1.77.32h.08a1.6 1.6 0 0 0 .97-1.47v-.17a1.9 1.9 0 0 1 3.8 0v.09a1.6 1.6 0 0 0 .97 1.47 1.6 1.6 0 0 0 1.77-.32l.06-.06a1.9 1.9 0 1 1 2.69 2.69l-.06.06a1.6 1.6 0 0 0-.32 1.77v.08a1.6 1.6 0 0 0 1.47.97h.17a1.9 1.9 0 0 1 0 3.8h-.09a1.6 1.6 0 0 0-1.47.97Z" />
    </svg>
  );
}

/*
 * The dashboard editor's own set (docs/dashboard-editing-design.md sections 4.3
 * and 6). These sit in a tile's corner where there is no room for a text label
 * beside them, so unlike the icons above each one is drawn with an
 * `aria-label` on the button that carries it -- the label names the component
 * it acts on, which is what makes a row of four identical verbs distinguishable
 * to a screen reader.
 */

/** The dotted grip every draggable thing has had since toolbars had handles. */
export function GripIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <circle cx="9" cy="6" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="6" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="9" cy="18" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="18" r="1.3" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Two offset sheets: a copy of what is already there. */
export function CopyIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M15 5.5A1.5 1.5 0 0 0 13.5 4h-8A1.5 1.5 0 0 0 4 5.5v8A1.5 1.5 0 0 0 5.5 15" />
    </svg>
  );
}

/** A bin, for removing a tile from the layout. Nothing is deleted from the
    server by this: the draft is not saved until the reader says so. */
export function TrashIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M4 7h16M10 4h4M6 7l1 12.5A1.5 1.5 0 0 0 8.5 21h7a1.5 1.5 0 0 0 1.5-1.5L18 7" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

/** An arrow curving back on itself. Mirrored for redo, so the pair reads as
    one gesture in two directions rather than two unrelated pictures. */
export function UndoIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M4 9h10a5.5 5.5 0 0 1 0 11h-4" />
      <path d="M7.5 5.5 4 9l3.5 3.5" />
    </svg>
  );
}

export function RedoIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M20 9H10a5.5 5.5 0 0 0 0 11h4" />
      <path d="M16.5 5.5 20 9l-3.5 3.5" />
    </svg>
  );
}

/** Blocks pulled up against a ceiling: the "tidy layout" action, which closes
    the gaps a rearrangement leaves behind (design section 3.5). */
export function TidyIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M4 4h16" />
      <rect x="4" y="7.5" width="7" height="6" rx="1.3" />
      <rect x="13" y="7.5" width="7" height="10" rx="1.3" />
      <path d="M7.5 20v-2.5M7.5 17.5 6 19M7.5 17.5 9 19" />
    </svg>
  );
}

/** A diagonal double-headed arrow: the corner grip that resizes a tile. */
export function ResizeIcon({ size = 13 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M20 10v10H10" />
      <path d="M20 20 12 12" />
    </svg>
  );
}

/**
 * A bell, filled when the answer is yes.
 *
 * The one pair in this file that changes shape rather than only colour.
 * Following is a state a reader has to be able to read at a glance down a
 * column of fifty rows, and colour alone is not something everybody can do
 * that with -- so the outline and the filled bell differ in weight as well,
 * and the button beside them says "Follow" or "Following" in words regardless.
 */
export function BellIcon({ size = 15, filled = false }: { size?: number; filled?: boolean }) {
  return (
    <svg {...base} width={size} height={size} fill={filled ? 'currentColor' : 'none'}>
      <path d="M6 9a6 6 0 0 1 12 0c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6Z" />
      <path d="M10 19a2 2 0 0 0 4 0" fill="none" />
    </svg>
  );
}

/** A sheet with a folded corner: a stored file, as opposed to a link off this
    deployment. The two sit side by side in the register, so they have to be
    tellable apart at 15 pixels. */
export function FileIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}

/** An arrow leaving its box: somewhere that is not here. */
export function ExternalLinkIcon({ size = 15 }: { size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6M21 3l-9 9" />
    </svg>
  );
}
